// Remembers which demo-lab PDFs have already been generated and uploaded for an entity.
//
// The three documents (Schema, Spotter reasoning, Crib sheet) are rendered in the browser and
// POSTed to `upload-pdf/`. Reaching the download step always goes through Next on the crib sheet,
// so reopening a finished flow would rebuild and re-upload documents the backend already stores -
// several seconds of spinner for files that do not change. This record lets that Next pass
// straight through, leaving the stored PDFs to be served by `files/download/`.
//
// Regenerating any step drops the record (see `regenerateStep` in dashboardApi): the content the
// PDFs describe has changed, so they have to be rebuilt before the ZIP is served again.
//
// There is no endpoint that lists an entity's uploaded files, so this is the only place that
// knowledge exists - which makes it per-browser. On another device the record is absent and the
// PDFs are rebuilt once, which is harmless: `upload-pdf/` overwrites by filename.

export type DemoLabPdfKind = "schema" | "questions" | "cribSheet";

type DemoLabEntityId = number | string | null | undefined;

type UploadedRecord = Partial<Record<DemoLabPdfKind, boolean>>;

const storageKey = (entityId: number | string) => `demolab_pdfs_uploaded_${entityId}`;

function hasEntityId(entityId: DemoLabEntityId): entityId is number | string {
    return entityId !== null && entityId !== undefined && entityId !== "";
}

function readRecord(entityId: DemoLabEntityId): UploadedRecord {
    if (!hasEntityId(entityId)) {
        return {};
    }

    try {
        const raw = localStorage.getItem(storageKey(entityId));

        if (!raw) {
            return {};
        }

        const parsed: unknown = JSON.parse(raw);

        return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
            ? (parsed as UploadedRecord)
            : {};
    } catch {
        // Storage unavailable (private mode/quota) or corrupt value: treat as nothing uploaded.
        return {};
    }
}

export function isDemoLabPdfUploaded(entityId: DemoLabEntityId, kind: DemoLabPdfKind): boolean {
    return readRecord(entityId)[kind] === true;
}

export function markDemoLabPdfUploaded(entityId: DemoLabEntityId, kind: DemoLabPdfKind): void {
    if (!hasEntityId(entityId)) {
        return;
    }

    try {
        localStorage.setItem(
            storageKey(entityId),
            JSON.stringify({ ...readRecord(entityId), [kind]: true })
        );
    } catch {
        // Storage unavailable: the PDF is uploaded, we just cannot remember it for next time.
    }
}

export function clearDemoLabPdfUploads(entityId: DemoLabEntityId): void {
    if (!hasEntityId(entityId)) {
        return;
    }

    try {
        localStorage.removeItem(storageKey(entityId));
    } catch {
        // Nothing to recover: a stale record only costs one rebuild on the next Next.
    }
}
